import { RuleValidationStatus, RuleValidationTargetType } from '@atlas/types'
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { toRuleValidationRow } from '@/adapters/rule-validation-row'
import type { DeliverableFile } from '@/domain/work-order.types'
import {
	isRunTerminal,
	ruleValidationRunQuery,
	ruleValidationRunsKey,
	ruleValidationRunsQuery,
} from '@/hooks/useRuleValidation'
import { notification } from '@/notifications/notification-store'
import { ruleValidationApi } from '@/services/rule-validation.service'
import type { DraftRuleCheckFile } from './DraftRuleChecks'

const FILE = RuleValidationTargetType.FILE

const time = (iso: string) => new Date(iso).getTime()

/**
 * Validate rules on an order's drafts: one file run per draft, each resolving the rules for the
 * draft's own classification.
 *
 * Results show only once every draft's latest run was requested after the newest draft, so a new
 * upload hides them until the next run. A removal hides them too, for the session: the remaining
 * drafts' runs still describe them, but the set they were read against is gone.
 */
export function useDraftRuleValidation(drafts: readonly DeliverableFile[]) {
	const qc = useQueryClient()
	const [hiddenRunIds, setHiddenRunIds] = useState<ReadonlySet<string>>(new Set())
	const latestRuns = useQueries({
		queries: drafts.map((draft) => ruleValidationRunsQuery(FILE, draft.fileId)),
	}).map((query) => query.data?.[0] ?? null)
	const details = useQueries({
		queries: latestRuns.flatMap((run) => (run ? [ruleValidationRunQuery(FILE, run.targetId, run.requestId)] : [])),
	})

	const request = useMutation({
		mutationFn: () => Promise.all(drafts.map((draft) => ruleValidationApi.request(FILE, draft.fileId))),
		onError: () => notification.error('Could not validate the rules. Please try again.'),
		// Awaited, so the action stays pending until the new runs are listed.
		onSettled: () =>
			Promise.all(drafts.map((draft) => qc.invalidateQueries({ queryKey: ruleValidationRunsKey(FILE, draft.fileId) }))),
	})

	const newestDraft = Math.max(0, ...drafts.map((draft) => time(draft.createdAt)))
	const current = latestRuns.every(
		(run) => run !== null && time(run.createdAt) > newestDraft && !hiddenRunIds.has(run.requestId),
	)
	const runByFile = new Map(details.flatMap((query) => (query.data ? [[query.data.targetId, query.data]] : [])))
	// A finished run whose records have not loaded yet is still running as far as the screen can tell.
	const isRunning =
		request.isPending ||
		(current && latestRuns.some((run) => run && (!isRunTerminal(run) || !runByFile.has(run.targetId))))
	const results: DraftRuleCheckFile[] | null =
		drafts.length && current && !isRunning
			? drafts.map((draft) => {
					const run = runByFile.get(draft.fileId)
					return {
						fileId: draft.fileId,
						fileName: draft.fileName,
						didFail: run?.status === RuleValidationStatus.FAILED,
						checks: (run?.results ?? []).map(toRuleValidationRow),
					}
				})
			: null

	return {
		results,
		isRunning,
		validate: () => request.mutate(),
		/** Call when a draft is removed. */
		hideResults: () => setHiddenRunIds(new Set(latestRuns.flatMap((run) => (run ? [run.requestId] : [])))),
	}
}
